import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Text,
  Button,
  Flex,
  IconButton,
  Skeleton,
  VStack,
} from '@chakra-ui/react';
import {
  RiSkipBackLine,
  RiSkipForwardLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCalendarEventLine,
} from 'react-icons/ri';
import Header from '../header/Header';
import EventCard from './eventCard';
import CreateEventModal from '../../components/modal/CreateEventModal';
import { useDisclosure } from '@chakra-ui/hooks';
import { useData } from '../../data';

const LoadingSkeleton = () => {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6} mt={4}>
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Box
          key={item}
          borderWidth="1px"
          borderRadius="lg"
          overflow="hidden"
          p={4}
          bg="white"
        >
          <Skeleton height="200px" mb={4} />
          <Skeleton height="20px" mb={2} />
          <Skeleton height="20px" mb={2} />
          <Skeleton height="20px" width="60%" />
        </Box>
      ))}
    </SimpleGrid>
  );
};

const EmptyState = ({ tabIndex }) => {
  return (
    <VStack spacing={4} py={8} px={4} textAlign="center">
      <Box as={RiCalendarEventLine} size="64px" color="gray.400" />
      <Text fontSize="xl" fontWeight="bold" color="gray.600">
        No Events Found
      </Text>
      <Text color="gray.500" maxW="md">
        {tabIndex === 0
          ? 'There are no events available at the moment. Check back later for new events!'
          : "You haven't created any events yet. Click the 'Create Event' button to get started!"}
      </Text>
    </VStack>
  );
};

const Events = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 20;

  const {
    data: AllEventsData,
    isLoading: isLoadingAllEvents,
    refetch: refetchEvents,
  } = useData(
    `events?page_no=${page}&page_size=${pageSize}`,
    30000,
    {},
    1,
    true,
  );

  const {
    data: MyEventsData,
    isLoading: isLoadingMyEvents,
    refetch: refetchMyEvents,
  } = useData(
    `events/mine?page_no=${page}&page_size=${pageSize}`,
    30000,
    {},
    1,
    false,
  );

  const {
    isOpen: openCreateEventModal,
    onOpen: onOpenCreateEventModal,
    onClose: onCloseCreateEventModal,
    onToggle: onToggleCreateEventModal,
  } = useDisclosure();

  useEffect(() => {
    if (tabIndex === 0) {
      setEvents(AllEventsData?.data?.events || []);
    } else {
      setEvents(MyEventsData?.data?.events || []);
    }

    setTotalPages(AllEventsData?.data?.pagination?.total_pages);
  }, [MyEventsData, AllEventsData, tabIndex]);

  // Handle tab change
  const handleTabsChange = (index) => {
    if (index === 0) {
      refetchEvents();
    } else {
      refetchMyEvents();
    }

    setTabIndex(index);
    setPage(1);
    setEvents([]);
  };

  const isLoading = isLoadingAllEvents || isLoadingMyEvents;
  return (
    <Box className="bg-gray-100 w-full min-h-screen">
      <Header title={'Events'} />

      <Box className="w-full p-4">
        <Box className="flex flex-col bg-white" px={{ base: 2, md: 6 }} py={4}>
          <Button
            size="md"
            onClick={onOpenCreateEventModal}
            bg="#134A70"
            color="white"
            _hover={{ bg: '#134A70' }}
            className="self-end"
          >
            Create Event
          </Button>

          <Tabs
            index={tabIndex}
            onChange={handleTabsChange}
            variant="enclosed"
            bg="white"
            w="full"
            mt={4}
            borderRadius="md"
            boxShadow="sm"
            className="w-full"
          >
            <TabList>
              <Tab
                _selected={{
                  borderBottom: '4px solid #134A70',
                  color: '#134A70',
                  fontWeight: 'bold',
                }}
                borderBottom="4px solid transparent"
              >
                All Events
              </Tab>
              <Tab
                _selected={{
                  borderBottom: '4px solid #134A70',
                  color: '#134A70',
                  fontWeight: 'bold',
                }}
                borderBottom="4px solid transparent"
              >
                My Events
              </Tab>
            </TabList>
            <TabPanels>
              {[0, 1].map((tab) => (
                <TabPanel
                  key={tab}
                  px={0}
                  height="calc(100vh - 250px)" // Adjust this value based on your header and other elements
                  overflowY="auto"
                  className="hide-scroll"
                >
                  {isLoading ? (
                    <LoadingSkeleton />
                  ) : events?.length === 0 ? (
                    <EmptyState tabIndex={tabIndex} />
                  ) : (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-6">
                      {events?.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          tabIndex={tabIndex}
                          refetchMyEvents={refetchMyEvents}
                          refetchEvents={refetchEvents}
                        />
                      ))}
                    </div>
                  )}

                  {events?.length > 0 && totalPages > 1 && (
                    <Flex justify="center" mt={6} gap={2} pb={4}>
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(1)}
                        isDisabled={page === 1}
                        icon={<RiSkipBackLine />}
                        aria-label="First page"
                      />
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(page - 1)}
                        isDisabled={page === 1}
                        icon={<RiArrowLeftSLine />}
                        aria-label="Previous page"
                      />
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          variant={page === i + 1 ? 'solid' : 'outline'}
                          bg={page === i + 1 ? '#134A70' : 'transparent'}
                          color={page === i + 1 ? 'white' : '#134A70'}
                          borderColor="#134A70"
                          onClick={() => setPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(page + 1)}
                        isDisabled={page === totalPages}
                        icon={<RiArrowRightSLine />}
                        aria-label="Next page"
                      />
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(totalPages)}
                        isDisabled={page === totalPages}
                        icon={<RiSkipForwardLine />}
                        aria-label="Last page"
                      />
                    </Flex>
                  )}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </Box>

      <CreateEventModal
        isOpen={openCreateEventModal}
        onClose={onCloseCreateEventModal}
        tabIndex={tabIndex}
        refetchEvents={refetchEvents}
        refetchMyEvents={refetchMyEvents}
      />
    </Box>
  );
};

export default Events;
